import React from "react";
import { useTranslation } from "react-i18next";
import ResultCard from "./ResultCard";
import CardSkeleton from "./CardSkeleton";
import ScreenCard from "../../../../../app/src/Components/common/ScreenCard/ScreenCard";
import { WIDGET_ACTION_SCREEN } from "../../../../../app/src/Utils/Constants";

// Una fila por sección en la vista agrupada (pantallas 02/03/05/10/12/14). "Ver más" solo si
// el contador supera las tarjetas visibles (contrato §2.4: `count > data.length`) y, además,
// solo si hay una búsqueda activa (texto y/o filtros de nodo/fecha): en la vista por defecto
// (sin texto ni filtros) no se pinta ni el botón ni el número de resultados de la sección.
//
// Sección "Páginas" (contrato §2.5, `kind: "screens"`): misma cabecera, pero la rejilla usa
// ScreenCard (la tarjeta de página extraída de los widgets list-screen* de la home) dentro de
// `.custom-grid-widget .grid-container` — las mismas clases (sin CSS module) que ya estiliza
// CarrouselStyle.css para ListScreenGridComponent, con un override propio en Buscar.css
// (`.bq-page .custom-grid-widget .grid-container`) que fija 8 tarjetas por línea para
// diferenciarla del resto de contenidos, sin tocar esa rejilla en Home/Detalle. Título con el
// `titleClassName` por defecto de ScreenCard (overlay sobre la imagen, como en ListScreenComponent
// / "ListScreenItem"): el equipo pidió ese estilo en vez del de ListScreenGridComponent (título
// debajo), que es como se había dejado antes. El wrapper lleva también la clase `GroupWidget`
// (además de `custom-grid-widget`) porque `.GroupWidget .PlaylistItemTitle` (más específica que
// `.PlaylistItemTitle` sola) es la que ancla el título abajo (`bottom:10px`); sin ella el título
// hereda el `.PlaylistItemTitle` base (`top:50%`, centrado vertical), que es lo que había pasado
// al copiar solo `custom-grid-widget`/`grid-container` del widget de home.
// Sin fecha/orden/chips de contenido aquí: una tarjeta de página no los tiene (contrato §4).
const SectionRow = ({ title, count, cards, kind = "contents", loading, onMore, hasSearched, cardFields }) => {
  const { t } = useTranslation();
  // "Ver más" (8h/8i): se pinta siempre que haya más resultados de los que se ven, tenga o no
  // la sección un id numérico de nodo. Sin id, entrar puede no dar una vista navegable (lo
  // resuelve BuscarPage.js al cargar la sección, no aquí) — pero eso ya no es motivo para
  // esconder el botón ni el contador de antemano. Además, ni el botón ni el contador se pintan
  // si no hay búsqueda activa (ni texto ni filtros; vista por defecto de la interfaz).
  const showMore = hasSearched && !loading && count > cards.length;
  const isScreens = kind === "screens";

  return (
    <div className="bq-section-row">
      <div className="bq-section-row-header">
        <div className="bq-section-row-title">
          <h2>{title}</h2>
          {hasSearched && (
            <>
              <span className="bq-section-row-count bq-only-desktop">{t("search.buscar.sectionResults", { count })}</span>
              <span className="bq-section-row-count bq-only-mobile">{count}</span>
            </>
          )}
        </div>
        {showMore && (
          <button type="button" className="bq-section-row-more" onClick={onMore}>
            {t("search.buscar.seeMore")}
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        )}
      </div>
      {isScreens ? (
        <div className="custom-grid-widget GroupWidget">
          <div className="grid-container" aria-busy={loading}>
            {loading
              ? Array.from({ length: cards.length || 5 }).map((_, i) => <CardSkeleton key={i} />)
              : cards.map((item) => <ScreenCard key={item.id} item={item} trigger={WIDGET_ACTION_SCREEN} />)}
          </div>
        </div>
      ) : (
        <div className="bq-grid" aria-busy={loading}>
          {loading
            ? Array.from({ length: cards.length || 5 }).map((_, i) => <CardSkeleton key={i} />)
            : cards.map((c) => <ResultCard key={c.id} content={c} cardFields={cardFields} />)}
        </div>
      )}
    </div>
  );
};

export default SectionRow;
